import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Play, Pause, Trophy, TrendingUp, ArrowRight, Headphones } from "lucide-react";
import { api } from "../lib/api";
import { usePlayer } from "../contexts/PlayerContext";
import FavoriteButton from "./FavoriteButton";

function formatPlays(n) {
    if (!n && n !== 0) return "0";
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
    return String(n);
}

export default function Top10Leaderboard() {
    const [tracks, setTracks] = useState([]);
    const [loading, setLoading] = useState(true);
    const { current, playing, play, toggle } = usePlayer();

    useEffect(() => {
        api.get("/tracks/playlist", { params: { classify: "top", limit: 10 } })
            .then(({ data }) => setTracks(data))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    if (!loading && tracks.length === 0) return null;

    return (
        <section className="relative border-t border-[#1c1c1c] overflow-hidden" data-testid="top10-leaderboard">
            {/* Decorative tribal gradient */}
            <div className="pointer-events-none absolute -top-32 -left-32 w-[480px] h-[480px] rounded-full bg-[#D4AF37]/10 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-32 -right-32 w-[480px] h-[480px] rounded-full bg-[#00A86B]/10 blur-3xl" />

            <div className="relative max-w-7xl mx-auto px-5 md:px-12 py-20 md:py-28">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
                    <div>
                        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37] mb-3 inline-flex items-center gap-2">
                            <Trophy className="w-3.5 h-3.5" /> Classement officiel
                        </p>
                        <h2 className="font-display text-3xl md:text-5xl font-semibold tracking-tight leading-tight">
                            Top 10 <span className="text-gradient-tribal">MBOA</span>.
                        </h2>
                        <p className="mt-3 text-sm md:text-base text-[#A8A8A8] max-w-xl">
                            Les sons les plus écoutés sur MBOAZICK depuis leur sortie. Une écoute est comptée quand quelqu'un lance le titre, une fois par demi-heure et par auditeur.
                        </p>
                    </div>
                    <Link to="/playlist" className="hidden md:inline-flex items-center gap-2 text-sm text-[#D4AF37] hover:gap-3 transition-all" data-testid="top10-see-all">
                        Voir toute la playlist <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>

                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {[...Array(6)].map((_, i) => (
                            <div key={i} className="h-20 bg-[#161616] rounded-xl animate-pulse" />
                        ))}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="top10-list">
                        {tracks.map((t, idx) => {
                            const rank = idx + 1;
                            const isCurrent = current?.id === t.id;
                            const isPlaying = isCurrent && playing;
                            const isPodium = rank <= 3;
                            const rankColor = rank === 1 ? "#D4AF37" : rank === 2 ? "#C0C0C0" : rank === 3 ? "#CD7F32" : "#6E6E6E";
                            return (
                                <div
                                    key={t.id}
                                    className={`group relative flex items-center gap-3 md:gap-4 p-3 md:p-4 rounded-xl border transition-all ${
                                        isPodium
                                            ? "bg-gradient-to-r from-[#161616] to-[#1c1c1c] border-[#D4AF37]/30 hover:border-[#D4AF37]/60"
                                            : "bg-[#161616] border-[#262626] hover:border-[#00A86B]/40"
                                    }`}
                                    data-testid={`top10-row-${rank}`}
                                >
                                    {/* Rank number */}
                                    <div
                                        className="font-display text-3xl md:text-4xl font-bold leading-none w-10 md:w-12 text-center shrink-0"
                                        style={{ color: rankColor, textShadow: isPodium ? `0 0 24px ${rankColor}40` : "none" }}
                                    >
                                        {rank.toString().padStart(2, "0")}
                                    </div>

                                    {/* Cover with play button */}
                                    <div className="relative shrink-0">
                                        {t.cover_url ? (
                                            <img decoding="async" src={t.cover_url} alt={t.title} className="w-14 h-14 md:w-16 md:h-16 rounded-md object-cover" loading="lazy" />
                                        ) : (
                                            <div className="w-14 h-14 md:w-16 md:h-16 rounded-md bg-gradient-to-br from-[#00d97e]/25 to-[#ffc83d]/25 border border-[#262626] flex items-center justify-center text-xl">
                                                🎵
                                            </div>
                                        )}
                                        <button
                                            onClick={() => (isCurrent ? toggle() : play(t, { queue: tracks }))}
                                            className={`absolute inset-0 bg-black/60 rounded-md flex items-center justify-center transition-opacity ${
                                                isPlaying ? "opacity-100" : "opacity-0 group-hover:opacity-100"
                                            }`}
                                            data-testid={`top10-play-${t.id}`}
                                            aria-label={`Écouter ${t.title}`}
                                        >
                                            {isPlaying ? <Pause className="w-5 h-5 text-[#00A86B]" /> : <Play className="w-5 h-5 text-[#00A86B] ml-0.5" />}
                                        </button>
                                    </div>

                                    {/* Title + artist + stats */}
                                    <Link to={`/track/${t.slug || t.id}`} className="min-w-0 flex-1">
                                        <p className="font-medium truncate hover:text-[#00A86B] text-sm md:text-base" data-testid={`top10-title-${rank}`}>
                                            {t.title}
                                        </p>
                                        <p className="text-[11px] md:text-xs text-[#A8A8A8] truncate">{t.artist_name} • {t.genre}</p>
                                        <div className="mt-1 inline-flex items-center gap-1.5 text-[11px] font-mono text-[#D4AF37]" data-testid={`top10-plays-${rank}`}>
                                            <Headphones className="w-3 h-3" />
                                            {formatPlays(t.plays)} écoutes
                                            {rank <= 3 && <TrendingUp className="w-3 h-3 ml-1" />}
                                        </div>
                                    </Link>

                                    {/* Buy + fav */}
                                    <div className="hidden sm:flex flex-col items-end gap-1.5 shrink-0">
                                        <div className="flex items-center gap-2">
                                            <FavoriteButton track={t} size="sm" />
                                            <Link
                                                to={`/track/${t.slug || t.id}`}
                                                className="inline-flex items-center gap-1.5 bg-[#00A86B] hover:bg-[#00C97E] text-[#0F0F0F] text-xs font-semibold px-3 py-1.5 rounded-full transition-colors"
                                                data-testid={`top10-buy-${rank}`}
                                            >
                                                Acheter
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                <div className="mt-10 text-center md:hidden">
                    <Link to="/playlist" className="inline-flex items-center gap-2 text-sm text-[#D4AF37]">
                        Voir toute la playlist <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            </div>
        </section>
    );
}
